/**
 * Web Vitals Tracking Utility
 *
 * Sends Core Web Vitals metrics to GTM dataLayer.
 * Uses dynamic import to avoid increasing initial bundle size.
 */

import type { Metric } from 'web-vitals';

/**
 * Push a web vital metric to GTM dataLayer
 */
function sendToGTM(metric: Metric): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'web_vitals',
    web_vital_name: metric.name,
    web_vital_value: metric.value,
    web_vital_delta: metric.delta,
    web_vital_id: metric.id,
    web_vital_rating: metric.rating,
    web_vital_navigation_type: metric.navigationType,
  });
}

/**
 * Initialize Web Vitals tracking.
 * Call this once in the app entry point (main.tsx).
 *
 * Tracks:
 * - LCP (Largest Contentful Paint) - target < 2.5s
 * - INP (Interaction to Next Paint) - target < 200ms (replaced FID in v4)
 * - CLS (Cumulative Layout Shift) - target < 0.1
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 */
export async function initWebVitals(): Promise<void> {
  if (typeof Array.prototype.at !== 'function') return;

  try {
    // Dynamic import to avoid increasing initial bundle.
    // May fail if user has stale cached bundle referencing old chunk hash after deployment.
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');

    // Core Web Vitals
    onCLS(sendToGTM);
    onINP(sendToGTM);
    onLCP(sendToGTM);

    // Additional metrics
    onFCP(sendToGTM);
    onTTFB(sendToGTM);
  } catch (error) {
    // Log in dev to catch actual bugs; silent in prod for expected cache misses
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Web Vitals failed to initialize:', error);
    }
  }
}
